import { useState, useCallback, useMemo } from "react";
import {
  Button,
  Input,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from "@heroui/react";
import { FaPlus, FaTrash, FaArrowUp, FaArrowDown } from "react-icons/fa6";
import { FilteredOfficeHandbook } from "@/app/components/handbooks/filteredOfficeHandbook";
import { useForm } from "react-hook-form";

export interface OfficeSelectionItem {
  office_id: number | null;
  workplace_id: number | null;
  price: number;
}

export interface OfficesSelectionFormData {
  offices: OfficeSelectionItem[];
}

interface OfficesSelectionFormProps {
  defaultValues?: OfficesSelectionFormData;
  onSubmit: (data: OfficesSelectionFormData) => void;
  modalErrors?: Array<{ field: string; message: string }>;
}

export function OfficesSelectionForm({
  defaultValues,
  onSubmit,
  modalErrors = [],
}: OfficesSelectionFormProps) {
  const [offices, setOffices] = useState<OfficeSelectionItem[]>(
    defaultValues?.offices && defaultValues.offices.length > 0
      ? defaultValues.offices
      : [{ office_id: null, workplace_id: null, price: 0 }],
  );

  const form = useForm<OfficesSelectionFormData>({
    defaultValues: {
      offices: offices,
    },
  });

  // Функция для получения списка ID офисов, которые нужно исключить для конкретной строки
  const getExcludedOfficeIds = useCallback(
    (currentIndex: number): number[] => {
      return offices
        .map((item, idx) => (idx !== currentIndex ? item.office_id : null))
        .filter((id): id is number => id !== null && id !== 0);
    },
    [offices],
  );

  const handleAddOffice = useCallback(() => {
    const newOffices = [
      ...offices,
      { office_id: null, workplace_id: null, price: 0 },
    ];
    setOffices(newOffices);
    form.setValue("offices", newOffices);
  }, [offices, form]);

  const handleRemoveOffice = useCallback(
    (index: number) => {
      if (offices.length <= 1) {
        return; // Нельзя удалить последний офис
      }
      const newOffices = offices.filter((_, i) => i !== index);
      setOffices(newOffices);
      form.setValue("offices", newOffices);
    },
    [offices, form],
  );

  const handleMoveUp = useCallback(
    (index: number) => {
      if (index === 0) return;
      const newOffices = [...offices];
      [newOffices[index - 1], newOffices[index]] = [
        newOffices[index],
        newOffices[index - 1],
      ];
      setOffices(newOffices);
      form.setValue("offices", newOffices);
    },
    [offices, form],
  );

  const handleMoveDown = useCallback(
    (index: number) => {
      if (index === offices.length - 1) return;
      const newOffices = [...offices];
      [newOffices[index], newOffices[index + 1]] = [
        newOffices[index + 1],
        newOffices[index],
      ];
      setOffices(newOffices);
      form.setValue("offices", newOffices);
    },
    [offices, form],
  );

  const handleOfficeChange = useCallback(
    (index: number, officeId: string | number | null) => {
      const newOffices = [...offices];
      newOffices[index] = {
        ...newOffices[index],
        office_id: officeId ? Number(officeId) : null,
        workplace_id: null, // Сбрасываем рабочее место при смене офиса
      };
      setOffices(newOffices);
      form.setValue("offices", newOffices);
    },
    [offices, form],
  );

  const handlePriceChange = useCallback(
    (index: number, price: number) => {
      const newOffices = [...offices];
      newOffices[index] = {
        ...newOffices[index],
        price: price,
      };
      setOffices(newOffices);
      form.setValue("offices", newOffices);
    },
    [offices, form],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const data = form.getValues();

      // Валидация: проверяем, что хотя бы один офис выбран
      const hasValidOffice = data.offices.some(
        (office) => office.office_id !== null && office.office_id !== 0,
      );

      if (!hasValidOffice) {
        form.setError("offices", {
          type: "manual",
          message: "Необходимо выбрать хотя бы один офис",
        });
        return;
      }

      // Убираем пустые офисы (где office_id не выбран)
      const validOffices = data.offices.filter(
        (office) => office.office_id !== null && office.office_id !== 0,
      );

      onSubmit({ offices: validOffices });
    },
    [form, onSubmit],
  );

  const hasError = useMemo(() => {
    return (
      modalErrors.some((error) => error.field === "offices") ||
      !!form.formState.errors.offices
    );
  }, [modalErrors, form.formState.errors.offices]);

  const errorMessage = useMemo(() => {
    const error = modalErrors.find((error) => error.field === "offices");
    return (
      error?.message || form.formState.errors.offices?.message?.toString() || ""
    );
  }, [modalErrors, form.formState.errors.offices]);

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="flex flex-col gap-4">
        <div className="flex flex-row justify-between items-center">
          <span className="text-lg font-semibold">Офисы</span>
          <Button
            type="button"
            startContent={<FaPlus />}
            color="primary"
            variant="bordered"
            onPress={handleAddOffice}>
            Добавить офис
          </Button>
        </div>

        {hasError && <div className="text-danger text-sm">{errorMessage}</div>}

        <div className="border rounded-lg overflow-hidden">
          <Table aria-label="Офисы" removeWrapper>
            <TableHeader>
              <TableColumn>Офис</TableColumn>
              <TableColumn>Цена</TableColumn>
              <TableColumn width={120}>Действия</TableColumn>
            </TableHeader>
            <TableBody>
              {offices.map((office, index) => (
                <TableRow
                  key={`office-${index}-${office.office_id || "empty"}`}>
                  <TableCell>
                    <FilteredOfficeHandbook
                      key={`handbook-${index}-${office.office_id || "empty"}-${getExcludedOfficeIds(index).join(",")}`}
                      isRequired
                      variant="bordered"
                      selectedKey={
                        office.office_id ? String(office.office_id) : ""
                      }
                      onSelectionChange={(key) =>
                        handleOfficeChange(index, key)
                      }
                      excludeOfficeIds={getExcludedOfficeIds(index)}
                      children={<></>}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      variant="bordered"
                      placeholder="Цена"
                      value={office.price.toString()}
                      onValueChange={(value) =>
                        handlePriceChange(index, Number(value) || 0)
                      }
                      startContent={<span className="text-default-400">₽</span>}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-row gap-1">
                      <Button
                        type="button"
                        isIconOnly
                        size="sm"
                        variant="light"
                        onPress={() => handleMoveUp(index)}
                        isDisabled={index === 0}>
                        <FaArrowUp />
                      </Button>
                      <Button
                        type="button"
                        isIconOnly
                        size="sm"
                        variant="light"
                        onPress={() => handleMoveDown(index)}
                        isDisabled={index === offices.length - 1}>
                        <FaArrowDown />
                      </Button>
                      <Button
                        type="button"
                        isIconOnly
                        size="sm"
                        variant="light"
                        color="danger"
                        onPress={() => handleRemoveOffice(index)}
                        isDisabled={offices.length <= 1}>
                        <FaTrash />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex gap-2 justify-end mt-4">
          <Button
            color="primary"
            type="submit"
            isDisabled={form.formState.isSubmitting}>
            Далее
          </Button>
        </div>
      </div>
    </form>
  );
}
